import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import {
  IoListOutline,
  IoAddOutline,
  IoDocumentTextOutline,
  IoPlayOutline,
  IoCheckmarkDoneOutline,
  IoCubeOutline,
  IoWalletOutline,
  IoReorderFourOutline,
} from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import { printProductionOrderStatus } from "../../utils/helpers";
import "./styles.css";

function StatCard({ icon, iconBg, value, label }) {
  return (
    <Card className="prod-stat-card h-100">
      <CardBody className="d-flex align-items-start gap-3">
        <div className="prod-stat-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        <div>
          <div className="prod-stat-value">{value}</div>
          <div className="prod-stat-label">{label}</div>
        </div>
      </CardBody>
    </Card>
  );
}

function EmptyState({ message }) {
  return (
    <div className="prod-empty-state">
      <IoListOutline size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
      <span>{message}</span>
    </div>
  );
}

function StatusCellRenderer({ value }) {
  return printProductionOrderStatus(value);
}

export default function HomePage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kpiData, setKpiData] = useState({
    totalBillsOfMaterials: 0,
    activeBillsOfMaterials: 0,
    draftOrders: 0,
    inProgressOrders: 0,
    doneOrders: 0,
    unitsProduced: 0,
    productionCost: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);

  const getHomeData = async () => {
    setLoading(true);
    const data = await get("/production/home");
    if (data?.success) {
      setKpiData({
        totalBillsOfMaterials: data.total_bills_of_materials,
        activeBillsOfMaterials: data.active_bills_of_materials,
        draftOrders: data.draft_orders,
        inProgressOrders: data.in_progress_orders,
        doneOrders: data.done_orders,
        unitsProduced: data.units_produced,
        productionCost: data.production_cost,
      });
      setRecentOrders(data.recent_orders ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    getHomeData();
  }, []);

  const ordersColDefs = useMemo(() => [
    { field: "order_number", headerName: t('production.orderNumber'), flex: 1, sortable: true },
    {
      field: "product.name",
      headerName: t('production.product'),
      flex: 2,
      sortable: true,
      valueGetter: (params) => params.data?.product?.name ?? "-",
    },
    { field: "quantity_planned", headerName: t('production.quantityPlanned'), flex: 1, sortable: true },
    { field: "quantity_produced", headerName: t('production.quantityProduced'), flex: 1, sortable: true },
    {
      field: "status",
      headerName: t('common.status'),
      flex: 1,
      sortable: true,
      cellRenderer: StatusCellRenderer,
    },
    {
      field: "created_at",
      headerName: t('common.date'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value),
    },
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);

  const quickActions = [
    {
      label: t('production.newProductionOrder'),
      icon: <IoAddOutline size={18} color="#6C63FF" />,
      path: "/production/orders/create",
    },
    {
      label: t('production.newBillOfMaterials'),
      icon: <IoDocumentTextOutline size={18} color="#20c997" />,
      path: "/production/bills-of-materials/create",
    },
    {
      label: t('sidebar.productionOrders'),
      icon: <IoReorderFourOutline size={18} color="#845ef7" />,
      path: "/production/orders",
    },
    {
      label: t('sidebar.billsOfMaterials'),
      icon: <IoListOutline size={18} color="#f76707" />,
      path: "/production/bills-of-materials",
    },
  ];

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('production.title')} onReload={getHomeData} />

        {loading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            <Row className="g-3 mb-3">
              <Col md="4">
                <StatCard
                  icon={<IoDocumentTextOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={kpiData.totalBillsOfMaterials.toLocaleString()}
                  label={t('sidebar.billsOfMaterials')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoPlayOutline size={18} color="#f76707" />}
                  iconBg="#fff4e6"
                  value={kpiData.inProgressOrders.toLocaleString()}
                  label={t('production.inProgressOrders')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoCheckmarkDoneOutline size={18} color="#2f9e44" />}
                  iconBg="#ebfbee"
                  value={kpiData.doneOrders.toLocaleString()}
                  label={t('production.completedOrders')}
                />
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col md="4">
                <StatCard
                  icon={<IoListOutline size={18} color="#845ef7" />}
                  iconBg="#f3f0ff"
                  value={kpiData.draftOrders.toLocaleString()}
                  label={t('production.draftOrders')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoCubeOutline size={18} color="#20c997" />}
                  iconBg="#e6faf5"
                  value={kpiData.unitsProduced.toLocaleString()}
                  label={t('production.unitsProduced')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoWalletOutline size={18} color="#fa5252" />}
                  iconBg="#fff0f0"
                  value={Number(kpiData.productionCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  label={t('production.productionCost')}
                />
              </Col>
            </Row>

            <Row className="g-3">
              <Col lg="8" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('production.recentOrders')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="p-0">
                    {recentOrders.length === 0 && !loading ? (
                      <EmptyState message={t('production.noProductionOrders')} />
                    ) : (
                      <div className="ag-theme-alpine prod-table-wrapper" style={{ height: 320 }}>
                        <AgGridReact
                          rowData={recentOrders}
                          columnDefs={ordersColDefs}
                          defaultColDef={defaultColDef}
                          animateRows={true}
                          loading={loading}
                          suppressCellFocus={true}
                          suppressPaginationPanel={true}
                          onRowClicked={(e) => navigate('/production/orders/view/' + e.data.id)}
                        />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="4" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('inventory.quickActions')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column gap-2">
                    {quickActions.map((action) => (
                      <button
                        key={action.path}
                        className="prod-quick-action-btn"
                        onClick={() => navigate(action.path)}
                      >
                        {action.icon}
                        {action.label}
                      </button>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        }
      </div>
    </SidebarLayout>
  );
}
